
































































































import Vue from 'vue';
import { OutlinedSelect, OutlinedTextInput } from '@/components/ds';
import ProuniFileUpload from '@/components/forms/prouni/ProuniFileUpload.vue';
import ProuniFamilyMemberCard from '@/components/forms/prouni/ProuniFamilyMemberCard.vue';
import {
  formatCurrency,
  formatNumberToCurrency,
  parseCurrencyToNumber,
} from '@/helpers/formatters';
import { ACTIVITY_TYPES, toYesNoLabel, YES_LABEL, NO_LABEL } from '@/helpers/prouniOptions';
import {
  ProuniApplication,
  ProuniDocument,
  ProuniDocumentType,
} from '@/store/modules/prouni/types';

const YES = YES_LABEL;
const NO = NO_LABEL;

interface MemberEntry {
  key: number;
  index: number;
  data: { id?: number; name?: string; income?: number | string };
  isValid: boolean;
}

export default Vue.extend({
  name: 'ProuniFamilyIncomeForm',
  components: { OutlinedSelect, OutlinedTextInput, ProuniFileUpload, ProuniFamilyMemberCard },
  props: {
    application: {
      type: Object as () => ProuniApplication | null,
      default: null,
    },
  },
  data() {
    const app = this.application || {};
    const hasAlimony = typeof app.hasAlimony === 'boolean' ? app.hasAlimony : undefined;
    const existingMembers = app.familyMembers || [];
    return {
      form: {
        income: app.income != null ? formatNumberToCurrency(Number(app.income)) : '',
        activityType: app.activityType || '',
        hasAlimony,
      },
      masks: { currency: formatCurrency },
      alimonyValue: toYesNoLabel(hasAlimony),
      documentTypes: ProuniDocumentType,
      uploaded: {
        incomeMonth1: (app.documents || []).some(
          d => d.documentType === ProuniDocumentType.ownIncomeProofMonth1,
        ),
        incomeMonth2: (app.documents || []).some(
          d => d.documentType === ProuniDocumentType.ownIncomeProofMonth2,
        ),
        incomeMonth3: (app.documents || []).some(
          d => d.documentType === ProuniDocumentType.ownIncomeProofMonth3,
        ),
        alimonyProof: (app.documents || []).some(
          d => d.documentType === ProuniDocumentType.alimonyProof,
        ),
      },
      nextKey: existingMembers.length,
      members: existingMembers.map((data, index) => ({
        key: index,
        index,
        data,
        isValid: false,
      })) as MemberEntry[],
    };
  },
  computed: {
    activityOptions(): string[] {
      return ACTIVITY_TYPES;
    },
    yesNoOptions(): string[] {
      return [YES, NO];
    },
    documents(): ProuniDocument[] {
      return (this.application && this.application.documents) || [];
    },
  },
  mounted() {
    // Emit the initial validity so revisiting a step (already filled) enables Continue.
    this.inputHandler();
  },
  methods: {
    setIncomeDone(kind: 'incomeMonth1' | 'incomeMonth2' | 'incomeMonth3', done: boolean) {
      this.uploaded[kind] = done;
      this.inputHandler();
    },
    setAlimonyDone(done: boolean) {
      this.uploaded.alimonyProof = done;
      this.inputHandler();
    },
    alimonyHandler(value: string) {
      this.form.hasAlimony = value === YES;
      this.inputHandler();
    },
    addMemberHandler() {
      this.members.push({
        key: this.nextKey,
        index: this.members.length,
        data: {},
        isValid: false,
      });
      this.nextKey += 1;
      this.inputHandler();
    },
    async removeMemberHandler({ index, id }: { index: number; id?: number }) {
      if (id) {
        try {
          await this.$store.dispatch('REMOVE_PROUNI_FAMILY_MEMBER', id);
        } catch (err) {
          this.$emit('error');
          return;
        }
      }
      this.members.splice(index, 1);
      this.members = this.members.map((entry, i) => ({ ...entry, index: i }));
      this.inputHandler();
    },
    memberChangeHandler(payload: { index: number; id?: number; isValid: boolean }) {
      const entry = this.members[payload.index];
      if (!entry) return;
      entry.isValid = payload.isValid;
      entry.data = { ...entry.data, id: payload.id };
      this.inputHandler();
    },
    memberPersistedHandler(payload: { index: number; id: number }) {
      const entry = this.members[payload.index];
      if (entry) entry.data = { ...entry.data, id: payload.id };
    },
    inputHandler() {
      const membersValid = this.members.every(member => member.isValid);
      const alimonyOk = !this.form.hasAlimony || this.uploaded.alimonyProof;

      const isValid = Boolean(
        this.form.income &&
          this.form.activityType &&
          this.uploaded.incomeMonth1 &&
          this.uploaded.incomeMonth2 &&
          this.uploaded.incomeMonth3 &&
          typeof this.form.hasAlimony === 'boolean' &&
          membersValid &&
          alimonyOk,
      );

      this.$emit(
        'input',
        { ...this.form, income: parseCurrencyToNumber(this.form.income) },
        isValid,
      );
    },
  },
});
